<template>
  <div class="date-field">
    <label class="date-field-label" :for="inputId">{{ label }}</label>
    <div class="date-input-row">
      <input
        :id="inputId"
        class="date-text-input"
        v-model="displayValue"
        :placeholder="placeholder"
        @change="syncInput"
      />
      <VueDatePicker
        v-model="isoValue"
        enable-seconds
        utc="preserve"
        timezone="utc"
        format="yyyy-MM-dd HH:mm:ss"
        model-type="iso"
        :clearable="false"
        :month-change-on-scroll="false"
        :teleport="true"
        @update:model-value="onDatePicked"
      >
        <template #dp-input>
          <button class="calendar-icon-btn" type="button">
            <icon-calendar color="shuttle-gray" />
          </button>
        </template>
        <template #clear-icon><!-- hidden --></template>
        <template #time-picker="{ time }">
          <div class="tp-time-slot-wrap">
            <input
              class="tp-time-slot-input"
              :value="formatTimeFromSlot(time)"
              placeholder="HH:mm"
              @change="applyTimeSlotInput"
            />
          </div>
        </template>
      </VueDatePicker>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, useId } from 'vue';
import { VueDatePicker } from '@vuepic/vue-datepicker';
import IconCalendar from '../../assets/icons/IconCalendar.vue';
import DateTimeUtils from '../../helpers/DateTimeUtils';

const inputId = `date-text-input-${useId()}`;

// VueDatePicker types hours/minutes/seconds as number | number[] to support
// range pickers; our picker is always single so we normalise to a scalar.
interface TimePickerValue {
  hours: number | number[];
  minutes: number | number[];
  seconds: number | number[];
}

const toNum = (v: number | number[]): number => (Array.isArray(v) ? (v[0] ?? 0) : v);

const props = withDefaults(
  defineProps<{
    modelValue: string | null;
    label: string;
    placeholder?: string;
  }>(),
  { placeholder: 'yyyy-MM-dd HH:mm:ss' }
);

const emit = defineEmits<{
  'update:modelValue': [value: string | null];
}>();

const isoValue = ref<string | null>(props.modelValue);
const displayValue = ref(isoToDisplayString(props.modelValue ?? ''));

watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal !== isoValue.value) {
      isoValue.value = newVal;
      displayValue.value = isoToDisplayString(newVal ?? '');
    }
  }
);

function isoToDisplayString(iso: string): string {
  if (!iso) return '';
  try {
    const d = DateTimeUtils.adjustDateToUTC(new Date(iso));
    return DateTimeUtils.formatDateWithConditionalSeconds(d);
  } catch {
    return '';
  }
}

function displayStringToIso(str: string): string | null {
  if (!str) return null;
  try {
    const d = new Date(str.trim().replace(' ', 'T') + 'Z');
    return isNaN(d.getTime()) ? null : d.toISOString();
  } catch {
    return null;
  }
}

const syncInput = () => {
  const iso = displayStringToIso(displayValue.value);
  isoValue.value = iso;
  emit('update:modelValue', iso);
};

const onDatePicked = (value: string) => {
  isoValue.value = value;
  displayValue.value = isoToDisplayString(value);
  emit('update:modelValue', value);
};

const formatTimeFromSlot = (time?: TimePickerValue): string => {
  if (!time) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  const h = toNum(time.hours);
  const m = toNum(time.minutes);
  const s = toNum(time.seconds);
  const base = `${p(h)}:${p(m)}`;
  return s !== 0 ? `${base}:${p(s)}` : base;
};

const applyTimeSlotInput = (event: Event) => {
  const parts = (event.target as HTMLInputElement).value.trim().split(':');
  const h = String(Math.min(23, Math.max(0, parseInt(parts[0] ?? '') || 0))).padStart(2, '0');
  const m = String(Math.min(59, Math.max(0, parseInt(parts[1] ?? '') || 0))).padStart(2, '0');
  const s = String(Math.min(59, Math.max(0, parseInt(parts[2] ?? '') || 0))).padStart(2, '0');
  const dateStr = (isoValue.value ?? new Date().toISOString()).split('T')[0];
  const newIso = `${dateStr}T${h}:${m}:${s}.000Z`;
  isoValue.value = newIso;
  displayValue.value = isoToDisplayString(newIso);
  emit('update:modelValue', newIso);
};
</script>

<style scoped lang="scss">
@use '../../assets/styles/colors' as colors;
@use 'sass:map';

$silver: map.get(colors.$colors, 'silver');

.date-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.date-field-label {
  color: map.get(colors.$colors, 'dusty-gray');
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
}

.date-input-row {
  align-items: center;
  display: flex;
  gap: 8px;
}

.date-text-input {
  border: 1px solid $silver;
  border-radius: 4px;
  box-shadow: 0 0 0 rgba(0, 0, 0, 0.25);
  color: map.get(colors.$colors, 'shuttle-gray');
  flex: 1;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  height: 40px;
  line-height: 20px;
  outline: none;
  overflow: hidden;
  padding: 0 12px;

  &:hover {
    background: map.get(colors.$colors, 'selago');
    border-color: map.get(colors.$colors, 'silver');
  }

  &:focus {
    border-color: map.get(colors.$colors, 'jewel');
    color: map.get(colors.$colors, 'bright-gray');
  }
}

.calendar-icon-btn {
  align-items: center;
  background: map.get(colors.$colors, 'white');
  border: 1px solid $silver;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  height: 40px;
  justify-content: center;
  padding: 1px;
  width: 40px;

  &:hover {
    background: map.get(colors.$colors, 'white-ice');
  }

  :deep(svg) {
    display: block;
    height: 20px;
    width: 20px;
  }
}
</style>

<style lang="scss">
/* stylelint-disable selector-class-pattern */
@use '../../assets/styles/colors' as colors;
@use 'sass:map';

// Time-picker slot — replaces the VueDatePicker scroll-wheel with a text input.
// Must be non-scoped because VueDatePicker teleports the slot to <body>.
.tp-time-slot-wrap {
  align-items: center;
  border-top: 1px solid map.get(colors.$colors, 'silver');
  display: flex;
  justify-content: center;
  margin-top: 8px;
  padding: 16px 12px;
}

.tp-time-slot-input {
  border: 1px solid map.get(colors.$colors, 'silver');
  border-radius: 4px;
  color: map.get(colors.$colors, 'shuttle-gray');
  font-size: 14px;
  font-weight: 400;
  height: 40px;
  outline: none;
  padding: 0 12px;
  text-align: center;
  width: 140px;

  &:focus {
    border-color: map.get(colors.$colors, 'jewel');
    color: map.get(colors.$colors, 'bright-gray');
  }

  &::placeholder {
    color: map.get(colors.$colors, 'dusty-gray');
  }
}

.dp__btn.dp__button {
  border-top: 1px solid map.get(colors.$colors, 'silver');
  color: map.get(colors.$colors, 'shuttle-gray');
  font-size: 12px;

  &:hover {
    background: map.get(colors.$colors, 'selago');
    color: map.get(colors.$colors, 'bright-gray');
  }
}
/* stylelint-enable selector-class-pattern */
</style>
